import { VuMediChatbotInline } from 'Chatbot';
import { getInterstitialStates } from 'Utils/dynamic-state';
import { VaiaProductTour } from 'Utils/product-tours';
import { UserStore } from 'Main/user-store';
import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

/**
 * Show AI assistant tooltip, product tour.
 *
 * If forced = true, a smaller version of the tooltip is displayed. This smaller version is
 * used to display AI features to users that have already seen the whole product tour.
 */
const showProductTour = (forced = false) => {
    const tour = VaiaProductTour(forced);

    window.setTimeout(() => {
        const handleTourDone = () => {
            trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_CLICKED);
            UserStore.saveVAIATooltipSeen(true);
        };

        tour.on('cancel', handleTourDone);
        tour.on('complete', handleTourDone);

        tour.start();
    }, 500);
};

export function setupChatbot(brightcovePlayer) {
    return new Promise((resolve) => {
        const container = document.querySelector('.js_vmChatbotInline');
        const mobileTriggerContainer = document.querySelector('.js_vmChatbotInlineMobileTrigger');

        if (!container || !mobileTriggerContainer) {
            resolve(false);
            return;
        }

        // `null` if decision is not resolved, true|false otherwise
        const shouldBlockTooltip = () => {
            const states = getInterstitialStates();

            if (states.findIndex((state) => typeof state.data === 'undefined') > -1) {
                return null;
            }

            // If at least one `state.data` is `true`, tooltip should be blocked because
            // interstitial is displayed
            return states.reduce((acc, state) => acc || state.data, false);
        };

        const finalize = (initialized, shouldBlockTooltip) => {
            // Don't show the tooltip if an interstitial should be displayed
            if (shouldBlockTooltip) {
                resolve(false);
                return;
            }

            // The final check before showing the tooltip is done by checking
            // if the server also said to display the product tour (tooltip)
            if (initialized && chatbot.showProductTour) {
                // If a tooltip has been seen by the user, force the minimal version
                showProductTour(tooltipSeen);
                resolve(true);
                return;
            }

            resolve(false);
        };

        // Determine whether the client app wants to show the tooltip
        const tooltipSeen = UserStore.loadVAIATooltipSeen();
        const tooltipForced = new URL(window.location.href).searchParams.get('ai-summary') !== null;
        const showTooltip = !tooltipSeen || tooltipForced;

        const chatbot = new VuMediChatbotInline(
            parseInt(context.videoId, 10),
            context.viewedUUID,
            brightcovePlayer,
            context.videoSlug,
            showTooltip,
        );

        chatbot
            .initialize(container, mobileTriggerContainer)
            .then((initialized) => {
                const handleDynamicStateChange = () => {
                    const shouldBlock = shouldBlockTooltip();

                    if (shouldBlock !== null) {
                        finalize(initialized, shouldBlock);
                        window.removeEventListener('dynamicstatechange', handleDynamicStateChange);
                    }
                };

                const shouldBlock = shouldBlockTooltip();

                if (shouldBlock === null) {
                    window.addEventListener('dynamicstatechange', handleDynamicStateChange);
                } else {
                    finalize(initialized, shouldBlock);
                }
            })
            .catch(() => resolve(false));
    });
}
